import React from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { Form as FinalForm } from 'react-final-form';
import classNames from 'classnames';
import { injectIntl, intlShape } from '../../util/reactIntl';
import * as validators from '../../util/validators';
import {
  Form,
  FieldTextInput,
  PrimaryButton,
  InfoTooltip,
  ConsentBasisFields,
  attestationFromValues,
} from '../../components';

import css from './AddAudienceByEmailForm.module.css';

const AddAudienceByEmailFormComponent = props => (
  <FinalForm
    {...props}
    render={fieldRenderProps => {
      const {
        rootClassName,
        className,
        handleSubmit,
        inProgress,
        ready,
        invalid,
        pristine,
        intl,
        form,
        values,
      } = fieldRenderProps;

      const emailsLabelText = intl.formatMessage({
        id: 'AddAudienceByEmailForm.emailsLabel',
      });
      const emailsHelp = intl.formatMessage({ id: 'AddAudienceByEmailForm.emailsHelp' });
      const emailsLabel = (
        <span className={css.labelWithTooltip}>
          {emailsLabelText}
          <InfoTooltip text={emailsHelp} placement="top" />
        </span>
      );
      const emailsPlaceholder = intl.formatMessage({
        id: 'AddAudienceByEmailForm.emailsPlaceholder',
      });
      const emailsRequiredMessage = intl.formatMessage({
        id: 'AddAudienceByEmailForm.emailsRequired',
      });
      const emailsRequired = validators.required(emailsRequiredMessage);

      const classes = classNames(rootClassName || css.root, className);
      const submitInProgress = inProgress;
      const submitReady = ready;
      // The server refuses contacts without a consent attestation, so the
      // button stays off until the basis is chosen and confirmed.
      const attestation = attestationFromValues(values);
      const submitDisabled = invalid || submitInProgress || pristine || !attestation;

      const onSubmit = async values => {
        await handleSubmit(values);
        // Reset form and clear validation state after successful submission
        form.restart();
      };

      return (
        <Form className={classes} onSubmit={onSubmit}>
          <div className={css.field}>
            <FieldTextInput
              id="emails"
              name="emails"
              type="text"
              label={emailsLabel}
              placeholder={emailsPlaceholder}
              validate={emailsRequired}
              className={css.input}
            />
          </div>

          <ConsentBasisFields className={css.field} values={values} idPrefix="byEmail" />

          <PrimaryButton
            className={css.submitButton}
            readyClassName={css.submitButtonReady}
            type="submit"
            inProgress={submitInProgress}
            ready={submitReady}
            disabled={submitDisabled}
          >
            {intl.formatMessage({
              id: 'AddAudienceByEmailForm.addAudience',
            })}
          </PrimaryButton>
        </Form>
      );
    }}
  />
);

AddAudienceByEmailFormComponent.defaultProps = {
  rootClassName: null,
  className: null,
  inProgress: false,
  ready: false,
};

const { bool, string } = PropTypes;

AddAudienceByEmailFormComponent.propTypes = {
  rootClassName: string,
  className: string,
  inProgress: bool,
  ready: bool,
  intl: intlShape.isRequired,
};

const AddAudienceByEmailForm = compose(injectIntl)(AddAudienceByEmailFormComponent);
AddAudienceByEmailForm.displayName = 'AddAudienceByEmailForm';

export default AddAudienceByEmailForm;
